import React, { createContext, useContext } from "react";
import { Audio, OffthreadVideo } from "remotion";

/**
 * The media elements, chosen by whoever is rendering.
 *
 * There are two renderers now and they do not accept the same components.
 * `@remotion/renderer` — the worker, headless Chrome, ffmpeg — wants Remotion's
 * own `<OffthreadVideo>` and `<Audio>`. `@remotion/web-renderer` — the browser,
 * canvas, WebCodecs — supports neither, and asks for `<Video>` and `<Audio>`
 * from `@remotion/media` instead.
 *
 * Both are swapped through one context rather than two, because they are one
 * decision: a renderer that needs the media package for video needs it for
 * audio as well, and splitting them is how you end up with the video swapped
 * and the audio forgotten — which is exactly what happened the first time.
 *
 * A context rather than a build flag, because both run in the same bundle: the
 * editor's `<Player>` and a client-side render are the same page.
 *
 * `@remotion/media` is deliberately not imported here. It is a dependency of
 * the app doing client-side rendering, not of this package, and importing it
 * would pull WebCodecs-dependent code into the worker, which has neither
 * WebCodecs nor any use for it. The app passes the components in.
 */
export type VideoComponent = React.ComponentType<{
  src: string;
  muted?: boolean;
  volume?: number | ((frame: number) => number);
  trimBefore?: number;
  style?: React.CSSProperties;
}>;

export type AudioComponent = React.ComponentType<{
  src: string;
  volume?: number | ((frame: number) => number);
  trimBefore?: number;
  loop?: boolean;
}>;

export interface MediaComponents {
  Video: VideoComponent;
  Audio: AudioComponent;
}

const MediaComponentsContext = createContext<MediaComponents | null>(null);

/**
 * Overrides the media elements for everything below.
 *
 * Unset — which is every existing caller, including the worker and the editor
 * preview — means Remotion's own, so nothing changes for anybody who does not
 * ask.
 */
export const MediaComponentsProvider: React.FC<{
  components: MediaComponents;
  children: React.ReactNode;
}> = ({ components, children }) => (
  <MediaComponentsContext.Provider value={components}>{children}</MediaComponentsContext.Provider>
);

const DEFAULTS: MediaComponents = {
  Video: OffthreadVideo as VideoComponent,
  Audio: Audio as AudioComponent,
};

export function useMediaComponents(): MediaComponents {
  return useContext(MediaComponentsContext) ?? DEFAULTS;
}

/** The video element alone, for the two sections that only need that one. */
export function useVideoComponent(): VideoComponent {
  return useMediaComponents().Video;
}

export function useAudioComponent(): AudioComponent {
  return useMediaComponents().Audio;
}
